import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

// Interface for Process Parameter Verification cycle data
export interface ProcessParameterVerificationCycleData {
  cycleNum: string;
  product: string;
  executiveName: string;
  parameters: {
    rpoSolidFat: string;
    invertSyrup: string;
    butter: string;
    blackjack: string;
    water: string;
    creamingTime: string;
    mixingTime: string;
    doughTemperature: string;
    standingTime: string;
    biscuitAppearance: string;
    stackHeight: string;
    dimensions: string;
    moulderRpm: string;
    sugarSaltCashewSprinkling: string;
    ingredientsOfBatch: string;
  };
}

// Interface for offline saved data
export interface ProcessParameterVerificationOfflineData {
  cycleNo: number;
  records: any[];
  timestamp: number;
  plantTourId?: string;
}

// Interface for the state
interface ProcessParameterVerificationState {
  // Fetched cycles from API
  fetchedCycles: ProcessParameterVerificationCycleData[];
  
  // Offline saved data
  offlineSavedData: ProcessParameterVerificationOfflineData[];
  
  // Loading states
  isLoading: boolean;
  isSyncing: boolean;
  
  // Error state
  error: string | null;
  
  // Last fetch timestamp
  lastFetchTimestamp: number | null;
}

// Initial state
const initialState: ProcessParameterVerificationState = {
  fetchedCycles: [],
  offlineSavedData: [],
  isLoading: false,
  isSyncing: false,
  error: null,
  lastFetchTimestamp: null,
};

// Create the slice
const ProcessParameterVerificationSlice = createSlice({
  name: 'ProcessParameterVerification',
  initialState,
  reducers: {
    // Add offline saved data
    addOfflineData: (state, action: PayloadAction<ProcessParameterVerificationOfflineData>) => {
      state.offlineSavedData.push(action.payload);
    },
    
    // Clear all offline data (for cancel operation)
    clearOfflineData: (state) => {
      state.offlineSavedData = [];
    },
    
    // Remove specific offline data item
    removeOfflineDataItem: (state, action: PayloadAction<number>) => {
      state.offlineSavedData = state.offlineSavedData.filter(
        item => item.cycleNo !== action.payload
      );
    },
    
    // Set fetched cycles
    setFetchedCycles: (state, action: PayloadAction<ProcessParameterVerificationCycleData[]>) => {
      // Deduplicate by cycleNum
      const byCycle: Record<string, ProcessParameterVerificationCycleData> = {} as any;
      for (const item of action.payload || []) {
        const key = item.cycleNum;
        if (!byCycle[key]) {
          byCycle[key] = item;
        }
      }
      state.fetchedCycles = Object.values(byCycle);
      state.lastFetchTimestamp = Date.now();
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Reset state
    resetState: (state) => {
      state.fetchedCycles = [];
      state.offlineSavedData = [];
      state.isLoading = false;
      state.isSyncing = false;
      state.error = null;
      state.lastFetchTimestamp = null;
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Set syncing state
    setSyncing: (state, action: PayloadAction<boolean>) => {
      state.isSyncing = action.payload;
    },
  },
});

// Export actions
export const {
  addOfflineData,
  clearOfflineData,
  removeOfflineDataItem,
  setFetchedCycles,
  clearError,
  resetState,
  setLoading,
  setSyncing,
} = ProcessParameterVerificationSlice.actions;

// Export selectors
export const selectProcessParameterVerificationState = (state: { ProcessParameterVerification: ProcessParameterVerificationState }) => state.ProcessParameterVerification;
export const selectOfflineDataCount = (state: { ProcessParameterVerification: ProcessParameterVerificationState }) => state.ProcessParameterVerification.offlineSavedData.length;
export const selectFetchedCycles = (state: { ProcessParameterVerification: ProcessParameterVerificationState }) => state.ProcessParameterVerification.fetchedCycles;
export const selectIsLoading = (state: { ProcessParameterVerification: ProcessParameterVerificationState }) => state.ProcessParameterVerification.isLoading;
export const selectIsSyncing = (state: { ProcessParameterVerification: ProcessParameterVerificationState }) => state.ProcessParameterVerification.isSyncing;
export const selectError = (state: { ProcessParameterVerification: ProcessParameterVerificationState }) => state.ProcessParameterVerification.error;

// Export reducer
export default ProcessParameterVerificationSlice.reducer;

